import { ANNOUNCEMENT_EMAIL_PREVIEW_ASSETS } from "./previews/assets";
import type {
    AdminAnnouncementEmailTemplateContext,
    AdminAnnouncementEmailTemplateKey,
} from "./types";

type RenderAnnouncementEmailHtmlInput = AdminAnnouncementEmailTemplateContext & {
    templateKey: AdminAnnouncementEmailTemplateKey;
    imageUrl?: string | null;
};

const COLORS = {
    background: "#4a4a4a",
    card: "#ffffff",
    text: "#2f3341",
    muted: "#616b7f",
    border: "#e5e7eb",
    accent: "#cc280c",
    footer: "#6f1808",
    panel: "#f8f6f5",
    soft: "#f8fafc",
} as const;

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const splitLines = (value: string) =>
    value
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);

const splitParagraphs = (value: string) =>
    value
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);

const renderParagraphs = (value: string) =>
    splitParagraphs(value)
        .map(
            (paragraph) => `
                <p style="margin:0 0 24px; font-size:17px; line-height:1.8; color:${COLORS.text};">
                    ${escapeHtml(paragraph).replace(/\n/g, "<br />")}
                </p>
            `
        )
        .join("");

const renderSectionTitle = (label: string) => `
    <p style="margin:0 0 12px; font-size:18px; line-height:1.4; font-weight:700; color:${COLORS.text};">
        ${escapeHtml(label)}
    </p>
`;

const renderList = (label: string, items: string[]) => {
    if (items.length === 0) return "";

    return `
        <div style="margin:0 0 28px; padding:22px 24px; border:1px solid ${COLORS.border}; border-radius:22px; background:${COLORS.soft};">
            ${renderSectionTitle(label)}
            <ul style="margin:0; padding:0 0 0 18px; color:${COLORS.text};">
                ${items
                    .map(
                        (item) => `
                            <li style="margin:0 0 10px; font-size:16px; line-height:1.7;">
                                ${escapeHtml(item)}
                            </li>
                        `
                    )
                    .join("")}
            </ul>
        </div>
    `;
};

const renderCallout = (label: string, body: string) => {
    if (!body.trim()) return "";

    return `
        <div style="margin:0 0 28px; padding:22px 24px; border:1px solid #f4c7ba; border-radius:22px; background:${COLORS.panel};">
            ${renderSectionTitle(label)}
            <p style="margin:0; font-size:16px; line-height:1.75; color:${COLORS.text};">
                ${escapeHtml(body)}
            </p>
        </div>
    `;
};

const renderImageBlock = (imageUrl?: string | null) => {
    if (!imageUrl?.trim()) return "";

    return `
        <div style="margin:0 0 32px;">
            <img
                src="${escapeHtml(imageUrl.trim())}"
                alt="Announcement image"
                style="display:block; width:100%; max-width:100%; height:auto; border:0; border-radius:24px;"
            />
        </div>
    `;
};

const renderGeneralContent = ({ message, fields }: RenderAnnouncementEmailHtmlInput) => `
    ${fields.openingLine?.trim() ? renderParagraphs(fields.openingLine.trim()) : ""}
    ${renderParagraphs(message)}
    ${renderList("Highlights", splitLines(fields.highlights || ""))}
    ${renderCallout("Next Step", fields.callToAction || "")}
`;

const renderServiceUpdateContent = ({ message, fields }: RenderAnnouncementEmailHtmlInput) => `
    ${renderCallout("Maintenance Window", fields.timeWindow || "")}
    ${renderParagraphs(message)}
    ${renderList("Affected Services", splitLines(fields.affectedServices || ""))}
    ${renderList("What To Do Next", splitLines(fields.nextSteps || ""))}
`;

const renderPromotionContent = ({ message, fields }: RenderAnnouncementEmailHtmlInput) => `
    ${
        fields.offerCode?.trim()
            ? `
                <div style="margin:0 0 28px; padding:24px; border-radius:24px; background:${COLORS.panel}; text-align:center;">
                    <p style="margin:0 0 8px; font-size:14px; line-height:1.4; letter-spacing:0.08em; text-transform:uppercase; color:${COLORS.muted};">
                        Offer Code
                    </p>
                    <p style="margin:0; font-size:34px; line-height:1.1; font-weight:800; color:${COLORS.accent};">
                        ${escapeHtml(fields.offerCode.trim())}
                    </p>
                </div>
            `
            : ""
    }
    ${renderParagraphs(message)}
    ${renderList("How To Redeem", splitLines(fields.redeemSteps || ""))}
    ${renderList("Terms", splitLines(fields.terms || ""))}
`;

const renderSafetyAlertContent = ({ message, fields }: RenderAnnouncementEmailHtmlInput) => `
    ${renderCallout("Affected Area", fields.incidentArea || "")}
    ${renderParagraphs(message)}
    ${renderList("Recommended Actions", splitLines(fields.recommendedActions || ""))}
    ${renderCallout("Support Contact", fields.supportContact || "")}
`;

const renderAccountUpdateContent = ({ message, fields }: RenderAnnouncementEmailHtmlInput) => `
    ${renderCallout("Account Status", fields.accountStatus || "")}
    ${renderParagraphs(message)}
    ${renderList("Next Steps", splitLines(fields.nextSteps || ""))}
    ${renderCallout("Support Contact", fields.supportContact || "")}
`;

const renderTemplateContent = (input: RenderAnnouncementEmailHtmlInput) => {
    switch (input.templateKey) {
        case "SERVICE_UPDATE":
            return renderServiceUpdateContent(input);
        case "PROMOTION":
            return renderPromotionContent(input);
        case "SAFETY_ALERT":
            return renderSafetyAlertContent(input);
        case "ACCOUNT_UPDATE":
            return renderAccountUpdateContent(input);
        case "GENERAL_ANNOUNCEMENT":
        default:
            return renderGeneralContent(input);
    }
};

export const renderAnnouncementEmailMarkup = (
    input: RenderAnnouncementEmailHtmlInput
) => `
    <div style="margin:0; padding:18px; background:${COLORS.background};">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
            <tr>
                <td align="center">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="680" style="width:100%; max-width:680px; border-collapse:separate; border-spacing:0; background:${COLORS.card}; border-radius:42px; overflow:hidden;">
                        <tr>
                            <td style="padding:36px 36px 0; text-align:center;">
                                <img
                                    src="${ANNOUNCEMENT_EMAIL_PREVIEW_ASSETS.headerImage}"
                                    alt="Ride2Five"
                                    style="display:block; width:100%; max-width:520px; height:auto; margin:0 auto; border:0;"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style="padding:40px 36px 0;">
                                <h1 style="margin:0 0 30px; font-size:48px; line-height:1.08; font-weight:800; color:${COLORS.text};">
                                    ${escapeHtml(input.title || "Ride2Five Update")}
                                </h1>
                                ${renderImageBlock(input.imageUrl)}
                                <p style="margin:0 0 22px; font-size:17px; line-height:1.8; color:${COLORS.text};">
                                    Hello,
                                </p>
                                ${renderTemplateContent(input)}
                                <p style="margin:10px 0 0; font-size:17px; line-height:1.8; color:${COLORS.text};">
                                    Best regards,<br />
                                    The Ride2Five Team
                                </p>
                            </td>
                        </tr>
                        <tr>
                            <td style="padding:36px;">
                                <div style="height:1px; background:${COLORS.border}; font-size:0; line-height:0;">&nbsp;</div>
                            </td>
                        </tr>
                        <tr>
                            <td style="padding:0 36px 36px;">
                                <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse; background:${COLORS.footer}; border-radius:28px;">
                                    <tr>
                                        <td style="padding:30px 24px; text-align:center;">
                                            <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="border-collapse:collapse;">
                                                <tr>
                                                    <td style="padding:0 10px;">
                                                        <img src="${ANNOUNCEMENT_EMAIL_PREVIEW_ASSETS.socialIcons.twitter}" alt="Twitter" width="30" height="30" style="display:block; width:30px; height:30px; border:0;" />
                                                    </td>
                                                    <td style="padding:0 10px;">
                                                        <img src="${ANNOUNCEMENT_EMAIL_PREVIEW_ASSETS.socialIcons.tiktok}" alt="TikTok" width="30" height="30" style="display:block; width:30px; height:30px; border:0;" />
                                                    </td>
                                                    <td style="padding:0 10px;">
                                                        <img src="${ANNOUNCEMENT_EMAIL_PREVIEW_ASSETS.socialIcons.instagram}" alt="Instagram" width="30" height="30" style="display:block; width:30px; height:30px; border:0;" />
                                                    </td>
                                                </tr>
                                            </table>
                                            <div style="margin-top:24px;">
                                                <img
                                                    src="${ANNOUNCEMENT_EMAIL_PREVIEW_ASSETS.footerLogo}"
                                                    alt="Ride2Five"
                                                    style="display:block; width:200px; max-width:100%; height:auto; margin:0 auto; border:0;"
                                                />
                                            </div>
                                            <p style="margin:24px 0 0; font-size:14px; line-height:1.6; color:rgba(255,255,255,0.88);">
                                                © 2026 Ride2Five, All Rights Reserved
                                            </p>
                                            <p style="margin:16px 0 0; font-size:14px; line-height:1.6;">
                                                <span style="color:#ffffff; text-decoration:underline;">Privacy Policy</span>
                                                <span style="display:inline-block; width:24px;"></span>
                                                <span style="color:#ffffff; text-decoration:underline;">Unsubscribe</span>
                                            </p>
                                        </td>
                                    </tr>
                                </table>
                            </td>
                        </tr>
                    </table>
                </td>
            </tr>
        </table>
    </div>
`;

export const renderAnnouncementEmailHtmlDocument = (
    input: RenderAnnouncementEmailHtmlInput
) => `
<!doctype html>
<html lang="en">
    <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${escapeHtml(input.title || "Ride2Five Update")}</title>
    </head>
    <body style="margin:0; padding:0; background:${COLORS.background};">
        ${renderAnnouncementEmailMarkup(input)}
    </body>
</html>
`.trim();
